import { t } from "@lingui/macro";
import { Check } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

type Plan = {
  name: string;
  price: string;
  period: string;
  description: string;
  badge?: string;
  highlights: string[];
  cta: string;
  href: string;
  featured?: boolean;
};

const plans = (): Plan[] => [
  {
    name: t`Free`,
    price: "$0",
    period: t`/mo`,
    description: t`No card required. Get a feel for how it works.`,
    badge: t`No card required`,
    highlights: [
      t`1 resume limit`,
      t`Limited AI keyword targeting`,
      t`All ATS-friendly templates`,
      t`3 PDF downloads`,
    ],
    cta: t`Get started free`,
    href: "/ai-generator",
  },
  {
    name: t`Pro`,
    price: "$29",
    period: t`/mo`,
    description: t`Full AI writers, unlimited resumes, and priority support.`,
    badge: t`Most popular`,
    featured: true,
    highlights: [
      t`Unlimited resumes`,
      t`Full AI writers & editors`,
      t`Unlimited PDF exports`,
      t`Job search & tracking`,
    ],
    cta: t`Start Pro trial`,
    href: "/pricing",
  },
  {
    name: t`Enterprise`,
    price: t`Custom`,
    period: "",
    description: t`For teams, universities, and workforce programs.`,
    highlights: [
      t`Bulk seats & admin dashboard`,
      t`Custom branding`,
      t`Dedicated support`,
      t`API access`,
    ],
    cta: t`Contact sales`,
    href: "/contact",
  },
];

export const PricingTeaserSection = () => {
  const items = plans();

  return (
    <section id="pricing-teaser" className="relative bg-white py-24 sm:py-32">
      <div className="container relative mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto mb-16 max-w-2xl text-center"
        >
          <div className="mb-6 flex justify-center">
            <span className="rounded-full bg-white border border-[#3e3431]/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97757] shadow-sm">
              {t`Pricing`}
            </span>
          </div>
          <h2 className="font-serif text-3xl font-extrabold tracking-tight text-[#3e3431] sm:text-4xl md:text-5xl">
            {t`Choose the plan that fits your job search`}
          </h2>
          <p className="mt-6 text-lg font-medium text-[#475569]">
            {t`Pays for itself with the time you'll save creating one resume.`}
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          {items.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={cn(
                "relative flex flex-col p-2 rounded-[2.5rem] border bg-white",
                plan.featured
                  ? "border-[#D97757]/30 shadow-[0_20px_60px_-15px_rgba(217,119,87,0.25)]"
                  : "border-[#3e3431]/10",
              )}
            >
              <div className={cn(
                "relative flex flex-col h-full rounded-[calc(2.5rem-0.5rem)] border p-6 sm:p-8 overflow-hidden",
                plan.featured
                  ? "border-[#D97757]/10 bg-[#fff9f6]"
                  : "border-transparent bg-[#fdfcfb]"
              )}>
                {plan.badge && (
                  <span
                    className={cn(
                      "mb-6 inline-flex w-fit rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider",
                      plan.featured
                        ? "bg-[#D97757] text-white"
                        : "bg-[#3e3431]/5 text-[#3e3431]/60",
                    )}
                  >
                    {plan.badge}
                  </span>
                )}
                <h3 className="text-xl font-bold text-[#3e3431]">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-serif text-5xl font-extrabold tracking-tight text-[#3e3431]">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-sm font-semibold text-[#3e3431]/50">{plan.period}</span>
                  )}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#475569] font-medium">
                  {plan.description}
                </p>
                <div className="my-8 h-px w-full bg-[#3e3431]/10" />
                <ul className="flex-1 space-y-4">
                  {plan.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-[#475569] font-medium">
                      <Check weight="bold" className="mt-0.5 size-4 shrink-0 text-[#14b88a]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className={cn(
                    "mt-10 w-full rounded-full py-6 font-bold shadow-sm transition-transform hover:scale-[1.02] active:scale-[0.98]",
                    plan.featured
                      ? "bg-[#D97757] text-white hover:bg-[#C86646] shadow-[0_10px_25px_-5px_rgba(217,119,87,0.4)]"
                      : "bg-[#3e3431] text-white hover:bg-[#2d2725]",
                  )}
                >
                  <Link to={plan.href}>{plan.cta}</Link>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
